import React, { useCallback, useEffect, useId, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBell, faXmark } from "@fortawesome/free-solid-svg-icons";
import config from "./config";
import { TEAM_CHANGE_EVENT } from "./teamChanges";

export default function TeamInviteNotifications({ enabled }) {
  const token = localStorage.getItem("token") || "";
  const userId = localStorage.getItem("userID") || "";
  const [pending, setPending] = useState({ token: "", userId: "", teamId: "", invites: [], credits: [] });
  const [dismissed, setDismissed] = useState([]);
  const dismissalCache = useRef(new Map());
  const [open, setOpen] = useState(false);
  const root = useRef(null);
  const button = useRef(null);
  const link = useRef(null);
  const id = useId();
  const location = useLocation();
  const current = enabled && pending.token === token && pending.userId === userId;
  const invites = current ? pending.invites.filter(key => !dismissed.includes(key)) : [];
  const credits = current ? pending.credits.filter(key => !dismissed.includes(key)) : [];
  const count = invites.length + credits.length;
  const notifications = [
    ...(invites.length ? [{ type: "invite", keys: invites, state: { openInviteModal: true }, action: "View team invites",
      text: invites.length === 1 ? "A team invite is awaiting approval." : `${invites.length} team invites are awaiting approval.` }] : []),
    ...(credits.length ? [{ type: "credit request", keys: credits, state: { openTeamManagement: true }, action: "View team management",
      text: credits.length === 1 ? "A team member requested more credits." : `${credits.length} team credit requests are awaiting approval.` }] : []),
  ];
  const bellLabel = invites.length && credits.length ? `${count} requests awaiting approval`
    : invites.length ? `${count} ${count === 1 ? "invite" : "invites"} awaiting approval`
      : `${count} ${count === 1 ? "credit request" : "credit requests"} awaiting approval`;

  const dismissedFor = useCallback(teamId => {
    const key = `signaltunerTeamNotificationDismissals:${userId}:${teamId}`;
    if (!dismissalCache.current.has(key)) {
      let saved;
      try { saved = JSON.parse(sessionStorage.getItem(key)); } catch { /* Keep in-memory dismissal when storage is unavailable. */ }
      dismissalCache.current.set(key, new Set(Array.isArray(saved) ? saved.filter(value => typeof value === "string") : []));
    }
    return { key, values: dismissalCache.current.get(key) };
  }, [userId]);
  function dismiss(notification, restoreFocus = false) {
    const { key, values } = dismissedFor(pending.teamId);
    notification.keys.forEach(value => values.add(value));
    const saved = [...values];
    try { sessionStorage.setItem(key, JSON.stringify(saved)); } catch { /* The in-memory cache still applies. */ }
    setDismissed(saved); setOpen(false);
    if (restoreFocus && count > notification.keys.length) button.current?.focus();
  }

  useEffect(() => {
    setOpen(false);
    setPending({ token, userId, teamId: "", invites: [], credits: [] });
    setDismissed([]);
    if (!enabled || !token || !userId) return;
    let active = true;
    let controller;
    let timeout;
    let queued = false;
    const clear = () => { if (active) setPending({ token, userId, teamId: "", invites: [], credits: [] }); };
    async function refresh() {
      if (!active || document.hidden) return;
      if (controller) { queued = true; return; }
      const request = new AbortController();
      controller = request;
      timeout = setTimeout(() => request.abort(), 15000);
      try {
        const response = await fetch(`${config.API_URL.replace(/\/+$/, "")}/api/team`, {
          method: "GET", signal: request.signal, cache: "no-store",
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!response.ok) throw new Error("Team unavailable");
        const summary = await response.json();
        if (String(summary?.currentUserId) !== userId || !["Owner", "Admin", "Member"].includes(summary?.role)
          || !Number.isInteger(summary.teamId) || summary.teamId <= 0 || !Array.isArray(summary.invitations)) throw new Error("Invalid team summary");
        if (!active) return;
        // The backend owns approval access: managers review invites; only Owners review credits.
        const manager = summary.role === "Owner" || summary.role === "Admin";
        const invites = manager ? summary.invitations.filter(invite => invite?.status === "Pending" && invite.invitationId != null)
          .map(invite => `invite:${invite.invitationId}`) : [];
        const credits = summary.role === "Owner" && Array.isArray(summary.creditRequests)
          ? summary.creditRequests.filter(request => request?.status === "Pending" && request.requestId)
            .map(request => `credit:${request.requestId}`) : [];
        const { values } = dismissedFor(summary.teamId);
        setDismissed([...values]);
        setPending({ token, userId, teamId: summary.teamId, invites, credits });
      } catch { clear(); }
      finally {
        clearTimeout(timeout);
        controller = null;
        if (active && queued) { queued = false; refresh(); }
      }
    }
    const changed = () => { clear(); refresh(); };
    refresh();
    const timer = setInterval(refresh, 30000);
    window.addEventListener(TEAM_CHANGE_EVENT, changed);
    window.addEventListener("focus", refresh);
    document.addEventListener("visibilitychange", refresh);
    return () => {
      active = false; clearInterval(timer); clearTimeout(timeout); controller?.abort();
      window.removeEventListener(TEAM_CHANGE_EVENT, changed);
      window.removeEventListener("focus", refresh);
      document.removeEventListener("visibilitychange", refresh);
    };
  }, [enabled, token, userId, dismissedFor]);

  useEffect(() => { setOpen(false); }, [location.key]);
  useEffect(() => { if (!count) setOpen(false); }, [count]);
  useEffect(() => {
    if (!open) return;
    link.current?.focus();
    const outside = event => { if (!root.current?.contains(event.target)) setOpen(false); };
    document.addEventListener("pointerdown", outside);
    return () => document.removeEventListener("pointerdown", outside);
  }, [open]);

  if (!count) return null;
  return <div className="navbar-notifications" ref={root}
    onBlur={event => { if (!event.currentTarget.contains(event.relatedTarget)) setOpen(false); }}
    onKeyDown={event => { if (event.key === "Escape") { event.preventDefault(); setOpen(false); button.current?.focus(); } }}>
    <button ref={button} type="button" className="navbar-notification-bell" aria-label={`Team notifications: ${bellLabel}`}
      aria-expanded={open} aria-controls={id} onClick={() => setOpen(value => !value)}>
      <FontAwesomeIcon icon={faBell} aria-hidden="true" />
    </button>
    {open && <section id={id} className="navbar-notification-panel" aria-label="Team notifications">
      <h2>Notifications</h2>
      <ul className="navbar-notification-list">{notifications.map((notification, index) => <li key={notification.type}>
        <Link ref={index === 0 ? link : undefined} to="/team" state={notification.state} onClick={() => dismiss(notification)}>
          <FontAwesomeIcon icon={faBell} aria-hidden="true" />
          <span>{notification.text}<small>{notification.action}</small></span>
        </Link>
        <button type="button" className="navbar-notification-dismiss" aria-label={`Dismiss ${notification.type} notification`} onClick={() => dismiss(notification, true)}>
          <FontAwesomeIcon icon={faXmark} aria-hidden="true" />
        </button>
      </li>)}</ul>
    </section>}
  </div>;
}
